import { Button, Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import { useCallback } from 'react';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { useEffect } from 'react';
import { useState } from 'react';
import api from '@/services/ApiService';
import { useMemo } from 'react';
import VmNode from '../../assets/vm/VmNode';
import UpsNode from '../pma/tabs/host/UpsNode';
import HostNode from '../pma/tabs/host/HostNode';

const nodeTypes = { vm: VmNode, ups: UpsNode, host: HostNode };
const VM_WIDTH = 230;
const VM_HEIGHT = 100;
const VM_AREA_HEIGHT = 400;
const HOST_GAP = 40;
const UPS_Y = -280;

function wouldCreateCycle(sourceId, targetId, nodes) {
  const visited = new Set();
  function dfs(cur) {
    if (cur === sourceId) return true;
    if (visited.has(cur)) return false;
    visited.add(cur);
    const deps = nodes.find((n) => n.id === cur)?.data?.dependencies || [];
    return deps.some(dfs);
  }
  return dfs(targetId);
}

export default function PmaShutdownSettings({ deployment }) {
  const [upsArr, setUpsArr] = useState([]);
  const [hostArr, setHostArr] = useState([]);
  const [vmArr, setVmArr] = useState([]);

  const fetchAllAssets = useCallback(async () => {
    try {
      const upsRes = await Promise.all(
        deployment.config.upsArr.map((id) => api.get(`/asset/${id}`)),
      );
      const upsData = upsRes.map((r) => r.data);
      setUpsArr(upsData);

      const hostIds = [...new Set(upsData.flatMap((u) => u.hosts || []))];
      const hostRes = await Promise.all(
        hostIds.map((id) => api.get(`/asset/${id}`)),
      );
      const hostData = hostRes.map((r) => r.data);
      setHostArr(hostData);

      const vmIds = [...new Set(hostData.flatMap((h) => h.vms || []))];
      const vmRes = await Promise.all(
        vmIds.map((id) => api.get(`/asset/${id}`)),
      );
      const vmData = vmRes.map((r) => r.data);
      setVmArr(vmData);
    } catch (err) {
      console.error(err);
      toast.error(<ToastMessage title="Fehler beim Laden der Assets" />);
    }
  }, [deployment]);

  useEffect(() => {
    if (deployment?.config?.upsArr?.length) {
      fetchAllAssets();
    }
  }, [deployment, fetchAllAssets]);

  const initialNodes = useMemo(() => {
    if (!upsArr.length && !hostArr.length && !vmArr.length) return [];

    const nodes = [];
    const hostPos = {};
    let accX = 0;

    hostArr.forEach((host, hostIndex) => {
      if (hostIndex > 0) accX += HOST_GAP;

      const hostVms = host.vms || [];
      const boxWidth = Math.max(1, hostVms.length) * VM_WIDTH;

      nodes.push({
        id: `vm-area-${host._id}`,
        type: 'group',
        position: { x: accX, y: 0 },
        data: null,
        draggable: false,
        selectable: false,
        style: {
          width: boxWidth,
          height: VM_AREA_HEIGHT + VM_HEIGHT,
          border: '2px dashed #888',
          borderRadius: 4,
          backgroundColor: 'rgba(0,0,0,0.03)',
          pointerEvents: 'none',
        },
      });

      nodes.push({
        id: host._id,
        type: 'host',
        position: { x: accX + boxWidth / 2 - VM_WIDTH / 2, y: -100 },
        data: { title: host.title, state: host.state },
        draggable: false,
      });

      hostVms.forEach((vmId, idx) => {
        const vm = vmArr.find((v) => v._id === vmId);
        if (!vm) return;
        nodes.push({
          id: vm._id,
          type: 'vm',
          parentNode: `vm-area-${host._id}`,
          extent: 'parent',
          position: {
            x: idx * VM_WIDTH,
            y: (100 - (vm?.shutdownThreshold || 0)) * 4,
          },
          data: {
            title: vm.title,
            state: vm.state,
            shutdownThreshold: Math.round(vm?.shutdownThreshold || 0),
            dependencies: vm.dependencies || [],
            excludeFromStart: !!vm.excludeFromStart,
            excludeFromStop: !!vm.excludeFromStop,
          },
          draggable: true,
          style: {
            width: VM_WIDTH * 0.6,
            height: VM_HEIGHT,
          },
        });
      });

      hostPos[host._id] = { startX: accX, width: boxWidth };
      accX += boxWidth;
    });

    const upsGroups = {};

    upsArr.forEach((u) => {
      const hostKey = (u.hosts || []).sort().join(',');
      if (!hostKey) return;

      if (!upsGroups[hostKey]) {
        upsGroups[hostKey] = [];
      }
      upsGroups[hostKey].push(u);
    });

    Object.values(upsGroups).forEach((group) => {
      const groupHosts = (group[0].hosts || []).filter((id) => hostPos[id]);
      if (!groupHosts.length) return;

      const left = Math.min(...groupHosts.map((id) => hostPos[id].startX));
      const right = Math.max(
        ...groupHosts.map((id) => hostPos[id].startX + hostPos[id].width),
      );

      const upsNodeWidth = VM_WIDTH * 0.8;
      const groupWidth =
        group.length * upsNodeWidth + (group.length - 1) * HOST_GAP;

      const groupCenterX = (left + right) / 2;
      const startX = groupCenterX - groupWidth / 2;

      group.forEach((u, idx) => {
        nodes.push({
          id: u._id,
          type: 'ups',
          position: {
            x: startX + idx * (upsNodeWidth + 2 * HOST_GAP),
            y: UPS_Y,
          },
          data: {
            title: u.title,
            state: u.state,
            estimatedCharge: u.estimatedCharge,
          },
          draggable: false,
        });
      });
    });

    return nodes;
  }, [upsArr, hostArr, vmArr]);

  const initialEdges = useMemo(() => {
    if (!upsArr.length && !hostArr.length && !vmArr.length) return [];

    const edges = [];

    upsArr.forEach((u) => {
      (u.hosts || []).forEach((hostId) => {
        edges.push({
          id: `e_ups_${u._id}_${hostId}`,
          source: u._id,
          target: hostId,
          sourceHandle: 'ups-source',
          targetHandle: 'host-target',
          selectable: false,
          deletable: false,
        });
      });
    });

    hostArr.forEach((h) => {
      (h.vms || []).forEach((vmId) => {
        edges.push({
          id: `${h._id}_${vmId}`,
          source: h._id,
          target: vmId,
          sourceHandle: 'host-source',
          targetHandle: 'vm-target',
          selectable: false,
          deletable: false,
        });
      });
    });

    vmArr.forEach((vm) => {
      (vm.dependencies || []).forEach((depId) => {
        const sameHost = hostArr.some(
          (h) =>
            (h.vms || []).includes(vm._id) && (h.vms || []).includes(depId),
        );
        if (!sameHost) return;

        edges.push({
          id: `dep_${vm._id}_${depId}`,
          source: vm._id,
          target: depId,
          animated: true,
          style: { strokeDasharray: '5 7' },
          markerEnd: {
            type: 'arrowclosed',
            width: 20,
            height: 20,
            color: 'black',
          },
        });
      });
    });

    return edges;
  }, [upsArr, hostArr, vmArr]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => setNodes(initialNodes), [initialNodes]);
  useEffect(() => setEdges(initialEdges), [initialEdges]);

  const onNodeDragStop = useCallback(
    (_e, node) => {
      if (node.type !== 'vm') return;
      const { y } = node.position;

      const shutdownThreshold = Math.max(
        0,
        Math.min(100, Math.round(100 - y / 4)),
      );
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, shutdownThreshold } }
            : n,
        ),
      );
    },
    [setNodes],
  );

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      const isVmToVm = sourceNode?.type === 'vm' && targetNode?.type === 'vm';
      if (!isVmToVm) return;

      if (wouldCreateCycle(params.source, params.target, nodes)) {
        toast.warn(<ToastMessage title="Kreisverbindung nicht erlaubt!" />);
        return;
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id === params.source
            ? {
                ...n,
                data: {
                  ...n.data,
                  dependencies: [...(n.data.dependencies || []), params.target],
                },
              }
            : n,
        ),
      );

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            style: { strokeDasharray: '5 7' },
            markerEnd: {
              type: 'arrowclosed',
              width: 20,
              height: 20,
              color: 'black',
            },
          },
          eds,
        ),
      );
    },
    [nodes, hostArr],
  );

  const onEdgesDelete = useCallback(
    (deleted) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.type !== 'vm') return n;

          const oldDeps = n.data.dependencies || [];
          const newDeps = oldDeps.filter(
            (depId) =>
              !deleted.some((e) => e.source === n.id && e.target === depId),
          );

          return oldDeps.length === newDeps.length
            ? n
            : { ...n, data: { ...n.data, dependencies: newDeps } };
        }),
      );
    },
    [setNodes],
  );

  const handleSave = async () => {
    try {
      const promises = nodes
        .filter((n) => n.type === 'vm')
        .map(async (node) => {
          const { data: vmFromApi } = await api.get(`/vm/${node.id}`);
          const { version, ...vmWithoutVersion } = vmFromApi;
          const updatedVm = {
            ...vmWithoutVersion,
            shutdownThreshold: node.data.shutdownThreshold,
            dependencies: node.data.dependencies,
            excludeFromStart: !!node.data.excludeFromStart,
            excludeFromStop: !!node.data.excludeFromStop,
          };
          return api.put(`/vm/${node.id}`, updatedVm);
        });

      await Promise.all(promises);
      toast.success(
        <ToastMessage title="Änderungen erfolgreich gespeichert" />,
      );
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler beim Speichern der Änderungen"
          subtitle={error?.response?.data?.message}
        />,
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">Systemeinstellungen</Typography>
        <Divider />
      </Box>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          deleteKeyCode={['Delete', 'Backspace']}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          snapToGrid
          snapGrid={[10, 10]}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <Background gap={20} size={0.5} />
          <MiniMap nodeColor={(n) => n.style?.background || '#999'} />
          <Controls position="bottom-left" showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ mt: 'auto', ml: 'auto' }} // This pushes the button to the bottom right
      >
        Speichern
      </Button>
    </Box>
  );
}
